import getConfig from 'next/config';
import Head from 'next/head';
import { SEOSegmentationPageType } from '@/__generated__/globalTypes';
import { seoSegmentationListingPage_seoSegmentationDetails_SEOSegmentationDetailsSuccess_segmentationDetails_seoPageMetadata as seoPageMetadata } from '@/__generated__/seoSegmentationListingPage';
import { usePageAttributes } from '@/components/contexts/PageAttributesContext';
import { getPageTitle } from './getPageTitle';

const {
  publicRuntimeConfig: { NEXT_PUBLIC_APP_URL },
} = getConfig();

interface PageMetaProps {
  hrefLangLinks?: seoPageMetadata['hrefLangLinks'];
  title: string;
  canonicalUrl: string;
  metaDescriptions: string;
  metaKeywords?: string;
  paginationPageCount?: number;
  paginationCurrentPage?: number;
}

export const PageMeta = ({
  hrefLangLinks,
  title,
  canonicalUrl,
  metaDescriptions,
  metaKeywords,
  paginationPageCount = 1,
  paginationCurrentPage = 1,
}: PageMetaProps) => {
  const { segmentNamePlural, pageType = null, city = null, state = null } = usePageAttributes();

  const logoPath = `${NEXT_PUBLIC_APP_URL}/img/care-no-tag.jpg?v=2`;
  const isNotFirstPage = paginationCurrentPage > 1;
  const isLocalPage = pageType === SEOSegmentationPageType.LOCAL;
  const isFirstPage = paginationCurrentPage === 1;

  const shouldNoIndexPage = isNotFirstPage && isLocalPage;
  const canonicalPageUrl = isFirstPage
    ? canonicalUrl
    : `${canonicalUrl}-page${paginationCurrentPage}`;

  const pageTitle = getPageTitle({
    defaultTitle: title,
    curPage: paginationCurrentPage,
    segmentNamePlural,
    city,
    state,
    pageType,
  });

  let nextLink = null;
  let prevLink = null;

  // Set next link if there's a next page
  if (paginationCurrentPage < paginationPageCount) {
    const nextPageNumber = paginationCurrentPage + 1;
    const pageUrlSuffix = isFirstPage ? '-page2' : `-page${nextPageNumber}`;
    nextLink = canonicalUrl + pageUrlSuffix;
  }
  // Set previous link if we're not on the first page
  if (paginationPageCount > 1) {
    prevLink =
      paginationCurrentPage === 2
        ? canonicalUrl
        : `${canonicalUrl}-page${paginationCurrentPage - 1}`;
  }

  const cleanHrefLangLinks = hrefLangLinks?.filter(
    (link): link is NonNullable<typeof link> => link !== null && link !== undefined
  );

  return (
    <Head>
      {cleanHrefLangLinks?.map(({ languageCode, countryCode, url }) => (
        <link key={url} rel="alternate" href={url} hrefLang={`${languageCode}-${countryCode}`} />
      ))}

      <title>{pageTitle}</title>
      <meta charSet="utf-8" />
      <meta httpEquiv="X-UA-Compatible" content="IE=edge,chrome=1" />
      <meta name="description" content={metaDescriptions} />
      {metaKeywords && <meta name="keywords" content={metaKeywords} />}
      {shouldNoIndexPage && <meta name="robots" content="noindex, follow" />}
      <link rel="canonical" href={canonicalPageUrl} />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="verify-v1" content="zz15lR180u9eCqTfur/9l+bMP75AampYtDEm6OnbkWY=" />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={logoPath} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:description" content={metaDescriptions} />
      <meta property="og:site_name" content="Care.com" />
      <meta content="image/jpeg" property="og:image:type" />
      <meta content="1080" property="og:image:width" />
      <meta content="1080" property="og:image:height" />
      <meta property="fb:page_id" content="6030284861" />

      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            logo: logoPath,
            name: 'Care.com',
            sameAs: [
              'https://www.facebook.com/caredotcom/',
              'https://twitter.com/CareDotCom',
              'https://www.instagram.com/careorg/',
              'https://www.linkedin.com/company/care-com',
            ],
            url: 'https://www.care.com',
          }),
        }}
      />
      {nextLink && <link rel="next" href={nextLink} />}
      {prevLink && <link rel="prev" href={prevLink} />}
    </Head>
  );
};
